import { useExpertVoicesInfiniteQuery } from '@brandhub/hooks/brandhubQueries';
import { useCurrentBrandHubDomainId } from '@brandhub/hooks/useCurrentBrandHubDomainId';
import { ExpertVoice } from '@brandhub/types/brandhub';
import { InputField } from '@components/form/input-field/InputField';
import { ThoughtLeadershipFormType } from '@mailings/components/creators/personalized-mailing-creator-wizard/types/mailingObjectivesForm.types';
import EditM from '@semcore/icon/Edit/m';
import { ButtonLink } from '@semcore/ui/button';
import DropdownMenu from '@semcore/ui/dropdown-menu';
import Skeleton from '@semcore/ui/skeleton';
import SpinContainer from '@semcore/ui/spin-container';
import { useDebounceCallback } from '@src/hooks/useDebounceCallback';
import { useHasMultipleBrandsAccess } from '@src/hooks/useHasMultipleBrandsAccess';
import { useInfiniteScroll } from '@src/hooks/useInfiniteScroll';
import { ChangeEvent, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const ThoughtLeadershipExpertName = () => {
  const { t } = useTranslation(['mailings']);

  const [searchValue, setSearchValue] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [brandHubDomainId] = useCurrentBrandHubDomainId();

  const hasMultipleBrandsAccess = useHasMultipleBrandsAccess();

  const { register, setValue } = useFormContext<ThoughtLeadershipFormType>();
  const expertVoiceId = useWatch<ThoughtLeadershipFormType>({ name: 'expertVoiceId' });

  const {
    data: expertVoicesData,
    isPlaceholderData,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useExpertVoicesInfiniteQuery({
    q: searchValue,
    ...(hasMultipleBrandsAccess && { brandHubDomainId }),
  });

  const loaderRef = useInfiniteScroll(fetchNextPage, isFetching);

  const expertVoices = expertVoicesData?.pages.flatMap((page) => page.expertVoices) ?? [];

  const updateSearchValue = useDebounceCallback((value: string) => {
    setSearchValue(value);
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateSearchValue(e.target.value);
  };

  const handleSelectExpert = (expert: ExpertVoice) => () => {
    setValue('expertVoiceId', expert.id);
    setValue('expertName', expert.name, { shouldValidate: true });
    setValue('expertRole', expert.role, { shouldValidate: true });
    setValue('expertIntroduction', expert.bio ?? '', { shouldValidate: true });
    setIsDropdownVisible(false);
    updateSearchValue('');
  };

  const handleEditExpert = () => {
    setValue('expertVoiceId', undefined);
  };

  return (
    <DropdownMenu
      stretch="fixed"
      visible={isDropdownVisible && expertVoices.length > 0 && !expertVoiceId}
      onVisibleChange={setIsDropdownVisible}>
      <DropdownMenu.Trigger className="w-full">
        <InputField
          addonRight={
            <ButtonLink
              addonLeft={EditM}
              aria-label={t(
                'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.editExpert',
              )}
              color="icon-primary-neutral"
              data-test="edit-expert-name-button"
              hidden={!expertVoiceId}
              onClick={handleEditExpert}
            />
          }
          autoComplete="off"
          data-test="expert-name-input"
          id="expert-name-input"
          placeholder={t(
            'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.placeholder',
          )}
          readOnly={!!expertVoiceId}
          size="l"
          {...register('expertName', { onChange: handleInputChange })}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Popper
        aria-label={t(
          'mailings:personalizedMailingCreatorWizard.objectivesForm.thoughtLeadership.expertName.dropdown',
        )}>
        <SpinContainer
          loading={isPlaceholderData}
          size="m">
          <DropdownMenu.List>
            {expertVoices.map((expert, index) => (
              <DropdownMenu.Item
                key={expert.id}
                data-test={`expert-voice-dropdown-menu-item-${index}`}
                onClick={handleSelectExpert(expert)}>
                <DropdownMenu.Item.Content>
                  <DropdownMenu.Item.Text ellipsis>{expert.name}</DropdownMenu.Item.Text>
                </DropdownMenu.Item.Content>
                <DropdownMenu.Item.Hint className="block truncate">
                  {expert.role}
                </DropdownMenu.Item.Hint>
              </DropdownMenu.Item>
            ))}
            {hasNextPage && (
              <div
                ref={loaderRef}
                className="flex items-center px-2">
                <Skeleton h={52}>
                  <Skeleton.Text
                    h="20px"
                    width="30%"
                    y="6px"
                  />
                  <Skeleton.Text
                    h="20px"
                    width="60%"
                    y="30px"
                  />
                </Skeleton>
              </div>
            )}
          </DropdownMenu.List>
        </SpinContainer>
      </DropdownMenu.Popper>
    </DropdownMenu>
  );
};
